// src/pages/sales/purchase-order-components/po-delivery-confirmation-components/FinalApprovalSection.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  Chip,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  Container
} from '@mui/material';
import {
  VerifiedUser as ApprovalIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../utils/permissions';

const FinalApprovalSection = ({ approval, setApproval, onApprove, onSave }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId, id: poId } = useParams();
  
  // Permission checks
  const { canViewFinalApproval, canApproveFinalDelivery } = usePermissions();
  const canView = canViewFinalApproval();
  const canApprove = canApproveFinalDelivery();

  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + (dateString.includes('T') ? '' : 'T12:00:00'));
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signatureTitle, setSignatureTitle] = useState('');
  const [supervisorNotes, setSupervisorNotes] = useState('');
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);

  useEffect(() => {
    if (poId && poId !== 'new') {
      fetchSummaryData();
    }
  }, [poId]);

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      // Fetch documents count
      const { data: documents, error: docsError } = await supabase
        .from('po_confirmation_documents')
        .select('id')
        .eq('purchase_order_id', poId);

      if (docsError) throw docsError;

      // Fetch delivery confirmation data
      const { data: confirmation, error: confirmError } = await supabase
        .from('po_delivery_confirmations')
        .select('*')
        .eq('purchase_order_id', poId)
        .single();

      if (confirmError && confirmError.code !== 'PGRST116') throw confirmError;


      setSummaryData({
        documentsCount: documents?.length || 0,
        deliveryComplete: confirmation?.delivery_complete || 'N/A',
        issueSeverity: confirmation?.issue_severity || 'None',
        satisfactionRating: confirmation?.satisfaction_rating || 0,
        invoiceSent: confirmation?.invoice_sent || 'N/A',
        paymentCollected: confirmation?.payment_collected || 'N/A',
        conditionWhenDelivered: confirmation?.condition_when_delivered || 'N/A',
        actualDeliveryDate: confirmation?.actual_delivery_date || 'N/A',
        inventoryDeducted: confirmation?.inventory_deducted || false,
        inventoryDeductedAt: confirmation?.inventory_deducted_at || null
      });

      // Load existing approval data if exists
      if (confirmation?.approved) {
        setApproval({
          approved: confirmation.approved,
          approvedBy: {
            name: confirmation.approval_signature_name,
            title: confirmation.approval_signature_title
          },
          approvedAt: confirmation.approved_at,
          notes: confirmation.approval_notes
        });
        setSupervisorNotes(confirmation.approval_notes || '');
      }

    } catch (err) {
      console.error('Error fetching summary data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSignatureDialog = () => {
    setSignatureDialogOpen(true);
  };

  const handleCloseSignatureDialog = () => {
    setSignatureDialogOpen(false);
    setSignatureName('');
    setSignatureTitle('');
  };

  const handleConfirmApproval = async () => {
    if (!signatureName.trim()) {
      alert('Please enter your name to sign');
      return;
    }

    setSaving(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Get user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();

      const signatureData = {
        name: signatureName,
        title: signatureTitle || 'Supervisor',
        date: new Date().toISOString(),
        typed: true
      };

      const approvalTimestamp = new Date().toISOString();

      // Check if record exists
      const { data: existing } = await supabase
        .from('po_delivery_confirmations')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      const payload = {
        approved: true,
        approved_by: user.id,
        approved_by_name: profile?.full_name || user.email,
        approved_by_email: profile?.email || user.email,
        approved_at: approvalTimestamp,
        approval_signature_name: signatureName,
        approval_signature_title: signatureTitle || 'Supervisor',
        approval_notes: supervisorNotes || null,
        final_approval_completed: true,
        status: 'approved',
        updated_at: approvalTimestamp
      };

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .update(payload)
          .eq('purchase_order_id', poId);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .insert({
            company_id: companyId,
            purchase_order_id: poId,
            created_by: user.id,
            ...payload
          });

        if (error) throw error;
      }

      // Update delivery status on the purchase order to "confirmed"
      const { error: poError } = await supabase
        .from('customer_purchase_orders')
        .update({
          delivery_status: 'confirmed',
          updated_at: approvalTimestamp
        })
        .eq('id', poId);

      if (poError) throw poError;

      // Update local state
      setApproval({
        ...approval,
        approved: true,
        approvedBy: signatureData,
        approvedAt: approvalTimestamp,
        notes: supervisorNotes
      });

      handleCloseSignatureDialog();
      
      // Reload completion flags and summary
      if (onSave) onSave();
      if (onApprove) onApprove(signatureData);

      // Refresh summary data to show approved state
      await fetchSummaryData();

      setSnackbar({ open: true, message: 'Delivery confirmation approved successfully!', severity: 'success' });

    } catch (err) {
      console.error('Error saving approval:', err);
      setSnackbar({ open: true, message: 'Failed to save approval. Please try again.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUnapprove = async () => {
    setSaving(true);

    try {
      // Clear approval fields in po_delivery_confirmations
      const { error: confirmError } = await supabase
        .from('po_delivery_confirmations')
        .update({
          approved: false,
          approved_by: null,
          approved_by_name: null,
          approved_by_email: null,
          approved_at: null,
          approval_signature_name: null,
          approval_signature_title: null,
          approval_notes: null,
          final_approval_completed: false,
          status: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('purchase_order_id', poId);

      if (confirmError) throw confirmError;

      // Reset delivery status on the purchase order back to pending
      const { error: poError } = await supabase
        .from('customer_purchase_orders')
        .update({
          delivery_status: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', poId);

      if (poError) throw poError;

      // Update local state
      setApproval({
        approved: false,
        approvedBy: null,
        approvedAt: null,
        notes: null
      });

      setSupervisorNotes('');
      setUnapproveDialogOpen(false);

      // Reload completion flags and summary
      if (onSave) onSave();

      // Refresh summary data to show unapproved state
      await fetchSummaryData();

      setSnackbar({ open: true, message: 'Approval has been removed successfully. Delivery status reset to "Pending".', severity: 'info' });

    } catch (err) {
      console.error('Error removing approval:', err);
      setSnackbar({ open: true, message: 'Failed to remove approval. Please try again.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Access Denied for View Permission
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ApprovalIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Final Approval Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Final Approval. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  const isApproved = approval?.approved;

  return (
    <Card sx={{
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: isApproved ? '#f0f9f4' : alpha(theme.palette.grey[50], 0.5),
      boxShadow: `0 4px 12px ${alpha(isApproved ? theme.palette.success.main : theme.palette.grey[500], 0.15)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 20px ${alpha(isApproved ? theme.palette.success.main : theme.palette.grey[500], 0.2)}`
      }
    }}>
      <CardContent sx={{ p: 4 }}>
        {/* Section Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}>
            <ApprovalIcon sx={{ fontSize: 28, color: 'warning.main' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'warning.main', mb: 0.5 }}>
              Final Approval
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supervisor reviews and approves delivery confirmation
            </Typography>
          </Box>
          {loading && (
            <CircularProgress size={24} />
          )}
          {isApproved && (
            <Chip
              icon={<CheckIcon />}
              label="APPROVED"
              color="success"
              sx={{ fontWeight: 500, px: 2, py: 2.5 }}
            />
          )}
        </Box>

        {/* Review Summary - Always Show */}
        <Paper sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          bgcolor: 'white'
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
            Review Summary
          </Typography>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                    Documents Uploaded
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    {summaryData?.documentsCount || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                    Actual Delivery Date
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {formatDate(summaryData?.actualDeliveryDate)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                    Delivery Complete
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: summaryData?.deliveryComplete === 'yes' ? 'success.main' : summaryData?.deliveryComplete === 'no' ? 'warning.main' : 'text.primary' }}>
                    {summaryData?.deliveryComplete === 'yes' ? 'Yes' : summaryData?.deliveryComplete === 'no' ? 'No' : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                    Condition
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                    {summaryData?.conditionWhenDelivered || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                    Issue Severity
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                    {summaryData?.issueSeverity || 'None'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                    Invoice Sent
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                    {summaryData?.invoiceSent === 'yes' ? 'Yes' : summaryData?.invoiceSent === 'no' ? 'No' : summaryData?.invoiceSent === 'pending' ? 'Pending' : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                    Payment Collected
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                    {summaryData?.paymentCollected === 'yes' ? 'Yes' : summaryData?.paymentCollected === 'no' ? 'No' : summaryData?.paymentCollected === 'partial' ? 'Partial' : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>

        {!isApproved ? (
          <>

            {/* Supervisor Notes */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>
                Supervisor Notes (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={supervisorNotes}
                onChange={canApprove ? (e) => setSupervisorNotes(e.target.value) : undefined}
                disabled={!canApprove}
                placeholder="Add any notes or comments about this delivery confirmation..."
                sx={{
                  opacity: canApprove ? 1 : 0.5,
                  pointerEvents: canApprove ? 'auto' : 'none',
                  cursor: canApprove ? 'text' : 'not-allowed',
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: 2
                  }
                }}
              />
            </Box>

            {/* Approval Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ApprovalIcon />}
                onClick={canApprove ? handleOpenSignatureDialog : undefined}
                sx={{
                  px: 6,
                  py: 2,
                  borderRadius: 3,
                  fontWeight: 500,
                  fontSize: '1rem',
                  opacity: canApprove ? 1 : 0.5,
                  pointerEvents: canApprove ? 'auto' : 'none',
                  cursor: canApprove ? 'pointer' : 'not-allowed',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.4)}`,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Approve & Sign Confirmation
              </Button>
            </Box>
          </>
        ) : (
          /* Approved State */
          <Box>
            <Paper sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              bgcolor: alpha(theme.palette.success.main, 0.05)
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CheckIcon sx={{ fontSize: 32, color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.dark', flex: 1 }}>
                  Delivery Confirmation Approved
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={canApprove ? () => setUnapproveDialogOpen(true) : undefined}
                  sx={{
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.error.main, 0.5),
                    color: 'error.main',
                    opacity: canApprove ? 1 : 0.5,
                    pointerEvents: canApprove ? 'auto' : 'none',
                    cursor: canApprove ? 'pointer' : 'not-allowed',
                    '&:hover': {
                      borderColor: 'error.main',
                      bgcolor: alpha(theme.palette.error.main, 0.05)
                    }
                  }}
                >
                  Unapprove
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500, display: 'block', mb: 1 }}>
                    Approved By
                  </Typography>
                  <Box sx={{
                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive",
                    fontSize: '28px',
                    color: theme.palette.success.dark,
                    mb: 0.5
                  }}>
                    {approval.approvedBy?.name}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {approval.approvedBy?.title}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500, display: 'block', mb: 1 }}>
                    Approval Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {approval.approvedAt ? new Date(approval.approvedAt).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }) : '—'}
                  </Typography>
                </Grid>

                {approval.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500, display: 'block', mb: 1 }}>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {approval.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Warning Alert for No Approval Permission */}
        {!canApprove && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              You don't have approval permissions
            </Typography>
            <Typography variant="caption">
              Contact your administrator to request approval permissions.
            </Typography>
          </Alert>
        )}

        {/* Signature Dialog */}
        <Dialog
          open={signatureDialogOpen}
          onClose={handleCloseSignatureDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: 'none'
            }
          }}
        >
          <DialogTitle sx={{
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
            fontWeight: 500,
            color: 'primary.main'
          }}>
            Supervisor Approval Signature
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 3 }}>
              By signing below, you confirm that you have reviewed all delivery information and approve this confirmation.
            </Typography>

            <TextField
              fullWidth
              label="Your Name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Enter your full name"
              sx={{ mb: 2 }}
              autoFocus
            />

            <TextField
              fullWidth
              label="Your Title"
              value={signatureTitle}
              onChange={(e) => setSignatureTitle(e.target.value)}
              placeholder="e.g., Operations Manager, Supervisor"
            />

            {/* Signature Preview */}
            {signatureName && (
              <Box sx={{ mt: 3, p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}` }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 500, display: 'block', mb: 1 }}>
                  Signature Preview
                </Typography>
                <Box sx={{
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive",
                  fontSize: '32px',
                  color: theme.palette.primary.main
                }}>
                  {signatureName}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {signatureTitle || 'Supervisor'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
            <Button onClick={handleCloseSignatureDialog} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmApproval}
              disabled={!signatureName.trim() || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
              }}
            >
              {saving ? 'Saving...' : 'Confirm Approval'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unapprove Confirmation Dialog */}
        <Dialog
          open={unapproveDialogOpen}
          onClose={() => setUnapproveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: 'none'
            }
          }}
        >
          <DialogTitle sx={{
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
            fontWeight: 500,
            color: 'error.main'
          }}>
            Remove Approval?
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to remove the approval for this delivery confirmation?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This will clear the approval signature, timestamp, and notes. The delivery confirmation will return to a pending state and will need to be re-approved.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
            <Button 
              onClick={() => setUnapproveDialogOpen(false)} 
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleUnapprove}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CloseIcon />}
              sx={{
                borderRadius: 2,
                px: 3
              }}
            >
              {saving ? 'Removing...' : 'Remove Approval'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default FinalApprovalSection;